import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3, Users, Target, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, ArrowLeft, Loader2, BookOpen, Star, Search, Filter,
  ChevronDown, ChevronRight, User, GraduationCap, Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherSkillAnalytics = ({ onBack, classLevel = "6", allowedSubjects }) => {
  const [loading, setLoading] = useState(true);
  const defaultSubjects = ['Mathematics', 'Science', 'English', 'Social Studies'];
  const subjects = (Array.isArray(allowedSubjects) && allowedSubjects.length > 0)
    ? allowedSubjects
    : defaultSubjects;
  const [selectedSubject, setSelectedSubject] = useState(subjects[0] || 'Mathematics');
  // If the parent passes "all" (multi-class teacher), default to Class 6 so we always show real data.
  const [activeClass, setActiveClass] = useState(classLevel && classLevel !== 'all' ? String(classLevel) : '6');
  const [classData, setClassData] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewMode, setViewMode] = useState('class'); // 'class' or 'student'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('all'); // 'all' or a chapter_id
  // Assessment type tab: skill_builder | unit | quarterly | half_yearly | annual
  const [assessmentType, setAssessmentType] = useState('skill_builder');
  const [mockOverview, setMockOverview] = useState(null);
  // Bucket card open/close state — closed by default, click header to reveal names
  const [openBuckets, setOpenBuckets] = useState({ below: false, average: false, top: false });
  const toggleBucket = (key) => setOpenBuckets(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    // Keep the local active class in sync if parent changes.
    if (classLevel && classLevel !== 'all') setActiveClass(String(classLevel));
  }, [classLevel]);

  useEffect(() => {
    loadClassData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, activeClass, assessmentType]);

  // Reset chapter filter whenever class/subject/type changes
  useEffect(() => {
    setSelectedChapter('all');
  }, [selectedSubject, activeClass, assessmentType]);

  const loadClassData = async () => {
    setLoading(true);
    try {
      if (assessmentType === 'skill_builder') {
        const response = await axios.get(
          `${API}/skill-assessment/teacher/class-overview?class_level=${activeClass}&subject=${selectedSubject}`
        );
        setClassData(response.data);
        setMockOverview(null);
      } else {
        const response = await axios.get(
          `${API}/teacher/class-overview/mock-exams?class_level=${activeClass}&subject=${selectedSubject}&exam_type=${assessmentType}`
        );
        setMockOverview(response.data);
        setClassData(null);
      }
    } catch (error) {
      console.error('Error loading class data:', error);
      setClassData(null);
      setMockOverview(null);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 60) return 'text-green-600 bg-green-100';
    if (score >= 35) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreBgColor = (score) => {
    if (score >= 60) return 'from-green-500 to-emerald-600';
    if (score >= 35) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-600';
  };

  // --- Source-aware data normalization (Skill Builder vs Mock Exams) ---
  const isSkillBuilder = assessmentType === 'skill_builder';
  const activeData = isSkillBuilder ? classData : mockOverview;

  // --- Chapter filter aware derivations (only meaningful for Skill Builder) ---
  const chapterEntries = isSkillBuilder ? Object.entries(classData?.chapter_stats || {}) : [];
  const isChapterFiltered = isSkillBuilder && selectedChapter !== 'all';
  const chapterStat = isChapterFiltered ? (classData?.chapter_stats?.[selectedChapter] || null) : null;

  // Students filtered by search AND (when Skill Builder + chapter) chapter score
  const filteredStudents = (activeData?.student_summary || [])
    .filter(s =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.roll_number.includes(searchTerm)
    )
    .map(s => {
      if (!isChapterFiltered) return s;
      const cs = s.chapter_scores?.[selectedChapter];
      if (!cs) return null; // student didn't attempt this chapter
      return { ...s, avg_score: cs.last_score, chapters_attempted: 1 };
    })
    .filter(Boolean);

  // KPI buckets honour chapter filter (Skill Builder) OR fall back to overall student_summary
  const studentsBelow50 = isChapterFiltered
    ? (chapterStat?.below_50 || 0)
    : filteredStudents.filter(s => s.avg_score < 35).length;
  const studentsAverage = isChapterFiltered
    ? (chapterStat?.average_50_75 || 0)
    : filteredStudents.filter(s => s.avg_score >= 35 && s.avg_score < 60).length;
  const studentsAbove75 = isChapterFiltered
    ? (chapterStat?.above_75 || 0)
    : filteredStudents.filter(s => s.avg_score >= 60).length;
  const attemptedCount = isChapterFiltered
    ? (chapterStat?.students_attempted || 0)
    : (activeData?.students_with_attempts || 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50">
      {/* Header - Blue themed to match sidebar */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-800 text-white px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-blue-600 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Class Overview</h1>
              <p className="text-blue-200 text-sm">Class {activeClass} Performance Overview</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setViewMode('class')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'class' ? 'bg-white text-blue-700 shadow' : 'text-white hover:bg-white/10'
                }`}
              >
                <BarChart3 size={16} className="inline mr-2" />
                Class View
              </button>
              <button
                onClick={() => setViewMode('student')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'student' ? 'bg-white text-blue-700 shadow' : 'text-white hover:bg-white/10'
                }`}
              >
                <Users size={16} className="inline mr-2" />
                Student View
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Assessment Type Tabs — Primary (Topic Tests + Unit Tests) */}
        <div className="bg-white rounded-xl shadow-sm p-2 mb-4 flex flex-wrap gap-2 items-center">
          {[
            { id: 'skill_builder', label: 'Topic Tests' },
            { id: 'unit', label: 'Unit Test' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setAssessmentType(tab.id)}
              data-testid={`assessment-type-${tab.id}`}
              className={`flex-1 min-w-[120px] py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
                assessmentType === tab.id
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
          {/* Major Exams dropdown */}
          <div className="ml-auto flex items-center gap-2 pr-2">
            <label className="text-xs font-semibold uppercase text-gray-500">Major Exams</label>
            <select
              value={['quarterly','half_yearly','annual'].includes(assessmentType) ? assessmentType : ''}
              onChange={(e) => { if (e.target.value) setAssessmentType(e.target.value); }}
              data-testid="major-exams-filter"
              className={`text-sm rounded-lg px-3 py-2 border ${['quarterly','half_yearly','annual'].includes(assessmentType) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
            >
              <option value="">Select…</option>
              <option value="quarterly">Quarterly</option>
              <option value="half_yearly">Half-Yearly</option>
              <option value="annual">Finals</option>
            </select>
          </div>
        </div>

        {/* Class + Subject + Chapter Tabs */}
        <div className="bg-white rounded-xl shadow-sm p-3 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-gray-500">Class</label>
            <select
              value={activeClass}
              onChange={(e) => setActiveClass(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="class-overview-class-select"
            >
              {['6', '7', '8', '9', '10'].map(c => (
                <option key={c} value={c}>Class {c}</option>
              ))}
            </select>
          </div>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex flex-1 gap-2 min-w-0">
            {subjects.map(subject => (
              <button
                key={subject}
                onClick={() => setSelectedSubject(subject)}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
                  selectedSubject === subject
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {subject}
              </button>
            ))}
          </div>
          {isSkillBuilder && (
            <>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold uppercase text-gray-500">Chapter</label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white max-w-[260px]"
                  data-testid="class-overview-chapter-select"
                >
                  <option value="all">All Chapters</option>
                  {chapterEntries.map(([id, stats]) => (
                    <option key={id} value={id}>{stats.chapter_name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* CLASS VIEW */}
        {viewMode === 'class' && (
          <div className="space-y-6">
            {/* Distribution bar — replaces the 4-tile KPI row.
                Single horizontal stacked bar showing red/amber/green split + counts on hover. */}
            {(() => {
              const total = (studentsBelow50 + studentsAverage + studentsAbove75) || 1;
              const pctBelow = (studentsBelow50 / total) * 100;
              const pctMid = (studentsAverage / total) * 100;
              const pctAbove = (studentsAbove75 / total) * 100;
              const hasAny = (studentsBelow50 + studentsAverage + studentsAbove75) > 0;
              const totalStudents = activeData?.total_students || 0;
              return (
                <div className="bg-white rounded-xl shadow-sm p-4" data-testid="performance-distribution-bar">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="inline-flex items-center gap-1.5 text-gray-700">
                        <Users size={16} className="text-teal-600" />
                        <strong className="text-gray-900">{totalStudents}</strong>
                        <span className="text-gray-500">total students</span>
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="text-gray-500">
                        <strong className="text-gray-800">{attemptedCount}</strong> attempted
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1.5" title="Below 35%">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                        <span className="text-gray-600">Below 35%</span>
                        <strong>{studentsBelow50}</strong>
                      </span>
                      <span className="inline-flex items-center gap-1.5" title="35–60%">
                        <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                        <span className="text-gray-600">35–60%</span>
                        <strong>{studentsAverage}</strong>
                      </span>
                      <span className="inline-flex items-center gap-1.5" title="Above 60%">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        <span className="text-gray-600">Above 60%</span>
                        <strong>{studentsAbove75}</strong>
                      </span>
                    </div>
                  </div>
                  {/* The bar */}
                  <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden flex" title={`${studentsBelow50} below · ${studentsAverage} average · ${studentsAbove75} top`}>
                    {hasAny ? (
                      <>
                        <div className="bg-red-500 h-full transition-all" style={{ width: `${pctBelow}%` }} />
                        <div className="bg-orange-500 h-full transition-all" style={{ width: `${pctMid}%` }} />
                        <div className="bg-emerald-500 h-full transition-all" style={{ width: `${pctAbove}%` }} />
                      </>
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    {isChapterFiltered ? (
                      <>Showing data for <strong>{chapterStat?.chapter_name}</strong>.</>
                    ) : isSkillBuilder ? (
                      <>Buckets are based on each student's average score across all chapters attempted. Use the <strong>Chapter</strong> filter above to drill in.</>
                    ) : (
                      <>Buckets are based on each student's average score across all {assessmentType === 'unit' ? 'Unit Tests' : assessmentType === 'quarterly' ? 'Quarterly exams' : assessmentType === 'half_yearly' ? 'Half-Yearly exams' : 'Finals'} attempted (across {mockOverview?.total_mock_exams || 0} exam{(mockOverview?.total_mock_exams || 0) === 1 ? '' : 's'}).</>
                    )}
                  </p>
                </div>
              );
            })()}

            {/* Quick Insights - 3 columns (each header carries its own count) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Needs Attention */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="bucket-card-below">
                <button
                  type="button"
                  onClick={() => toggleBucket('below')}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between hover:brightness-110 transition-all"
                  data-testid="bucket-toggle-below"
                >
                  <h2 className="text-white font-semibold flex items-center gap-2">
                    <AlertTriangle size={20} />
                    Needs Attention (Below 35%) — {studentsBelow50} student{studentsBelow50 === 1 ? '' : 's'}
                  </h2>
                  {openBuckets.below ? <ChevronDown size={18} className="text-white" /> : <ChevronRight size={18} className="text-white" />}
                </button>
                {openBuckets.below && (
                <div className="p-4 max-h-[300px] overflow-y-auto">
                  {filteredStudents.filter(s => s.avg_score < 35).length > 0 ? (
                    <div className="space-y-2">
                      {filteredStudents.filter(s => s.avg_score < 35).map((student, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                              <User size={20} className="text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{student.name}</p>
                              <p className="text-xs text-gray-500">Roll: {student.roll_number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-red-600">{student.avg_score}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="mx-auto mb-2 text-green-500" size={40} />
                      <p>No students below 35%</p>
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Average Students */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="bucket-card-average">
                <button
                  type="button"
                  onClick={() => toggleBucket('average')}
                  className="w-full bg-gradient-to-r from-orange-400 to-orange-500 px-6 py-4 flex items-center justify-between hover:brightness-110 transition-all"
                  data-testid="bucket-toggle-average"
                >
                  <h2 className="text-white font-semibold flex items-center gap-2">
                    <TrendingUp size={20} />
                    Average (35-60%) — {studentsAverage} student{studentsAverage === 1 ? '' : 's'}
                  </h2>
                  {openBuckets.average ? <ChevronDown size={18} className="text-white" /> : <ChevronRight size={18} className="text-white" />}
                </button>
                {openBuckets.average && (
                <div className="p-4 max-h-[300px] overflow-y-auto">
                  {filteredStudents.filter(s => s.avg_score >= 35 && s.avg_score < 60).length > 0 ? (
                    <div className="space-y-2">
                      {filteredStudents.filter(s => s.avg_score >= 35 && s.avg_score < 60).map((student, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                              <User size={20} className="text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{student.name}</p>
                              <p className="text-xs text-gray-500">Roll: {student.roll_number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-orange-600">{student.avg_score}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="mx-auto mb-2 text-orange-400" size={40} />
                      <p>No average students</p>
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Top Performers */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="bucket-card-top">
                <button
                  type="button"
                  onClick={() => toggleBucket('top')}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between hover:brightness-110 transition-all"
                  data-testid="bucket-toggle-top"
                >
                  <h2 className="text-white font-semibold flex items-center gap-2">
                    <Award size={20} />
                    Top Performers (Above 60%) — {studentsAbove75} student{studentsAbove75 === 1 ? '' : 's'}
                  </h2>
                  {openBuckets.top ? <ChevronDown size={18} className="text-white" /> : <ChevronRight size={18} className="text-white" />}
                </button>
                {openBuckets.top && (
                <div className="p-4 max-h-[300px] overflow-y-auto">
                  {filteredStudents.filter(s => s.avg_score >= 60).length > 0 ? (
                    <div className="space-y-2">
                      {filteredStudents.filter(s => s.avg_score >= 60).map((student, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              {idx === 0 ? <Award size={20} className="text-yellow-500" /> : <User size={20} className="text-green-600" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{student.name}</p>
                              <p className="text-xs text-gray-500">Roll: {student.roll_number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">{student.avg_score}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="mx-auto mb-2" size={40} />
                      <p>No top performers yet</p>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STUDENT VIEW */}
        {viewMode === 'student' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by name or roll number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Student List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <Users size={20} />
                  Student Performance ({filteredStudents.length} {filteredStudents.length === 1 ? 'student' : 'students'})
                </h2>
              </div>
              {filteredStudents.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Users className="mx-auto mb-2 text-gray-300" size={48} />
                  <p className="font-medium text-gray-700 mb-1">
                    No skill assessment attempts yet for Class {activeClass} — {selectedSubject}
                  </p>
                  <p className="text-sm">
                    Students will appear here once they attempt a skill assessment from the Student Portal.
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    Tip: For per-student deep-dive with AI Action Plan, open <strong>Student Review &amp; AI Plan</strong> from the sidebar.
                  </p>
                </div>
              )}
              <div className="divide-y">
                {filteredStudents.map((student, idx) => (
                  <div 
                    key={idx}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedStudent(selectedStudent?.name === student.name ? null : student)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          student.avg_score >= 60 ? 'bg-green-100' :
                          student.avg_score >= 35 ? 'bg-yellow-100' : 'bg-red-100'
                        }`}>
                          <span className={`text-lg font-bold ${
                            student.avg_score >= 60 ? 'text-green-600' :
                            student.avg_score >= 35 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {student.roll_number}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{student.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${
                            student.avg_score >= 60 ? 'text-green-600' :
                            student.avg_score >= 35 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {student.avg_score}%
                          </p>
                          <p className="text-xs text-gray-500">Average</p>
                        </div>
                        {selectedStudent?.name === student.name ? 
                          <ChevronDown size={20} className="text-gray-400" /> : 
                          <ChevronRight size={20} className="text-gray-400" />
                        }
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {selectedStudent?.name === student.name && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="grid grid-cols-2 gap-4">
                          {/* Weak Chapters */}
                          <div className="p-4 bg-red-50 rounded-lg">
                            <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                              <AlertTriangle size={16} />
                              Weak Chapters
                            </h4>
                            {student.weak_chapters.length > 0 ? (
                              <ul className="space-y-1">
                                {student.weak_chapters.map((ch, i) => (
                                  <li key={i} className="text-sm text-red-700">• {ch}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">No weak chapters</p>
                            )}
                          </div>
                          
                          {/* Strong Chapters */}
                          <div className="p-4 bg-green-50 rounded-lg">
                            <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                              <Star size={16} />
                              Strong Chapters
                            </h4>
                            {student.strong_chapters.length > 0 ? (
                              <ul className="space-y-1">
                                {student.strong_chapters.map((ch, i) => (
                                  <li key={i} className="text-sm text-green-700">• {ch}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">No strong chapters yet</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Recommendation */}
                        <div className={`mt-4 p-4 rounded-lg ${
                          student.avg_score >= 75 ? 'bg-green-100 text-green-800' :
                          student.avg_score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          <p className="font-medium">
                            {student.avg_score >= 75 ? '🌟 Excellent! Encourage to help classmates.' :
                             student.avg_score >= 50 ? '📚 Good progress. Focus on weak chapters.' :
                             '💪 Needs support. Recommend Audio Learning for basics.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherSkillAnalytics;
